import React, { useEffect, useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { Link } from 'react-router-dom'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import EffortChip from '../components/EffortChip'
import { fadeRightStyle, useHorizontalOverflow } from '../hooks/useHorizontalOverflow'
import { formatCompact } from '../utils/numbers'
import {
  getLlmRateHeadroom,
  getLlmUsage,
  getLlmUsageByUser,
  getRecentLlmCalls,
  LlmModelHeadroom,
  LlmUsageSplitBucket,
  LlmRecentCall,
  LlmUsageBreakdown,
  LlmUsageByUser,
  LlmUsageResponse,
  LlmUsageWindow,
} from '../api/client'

const WINDOWS: { value: LlmUsageWindow; label: string }[] = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
]

// Rows per page of the per-user table, which pages and filters on the server:
// a window holds thousands of user ids, most of them long-deleted test users.
const BY_USER_PAGE_SIZE = 25

const fmt = (n: number) => n.toLocaleString()
// The y-axes label in compact numbers (1.5M, not 1,500,000). Full figures were
// wider than recharts' default 60px axis once a total passed eight digits, and
// the leading digit fell off the chart's left edge ("0,000,000" for 10M).
// Compact labels stay within four or five characters, so the axis can be
// narrower than the default and give the plot the room back. Tooltips keep
// the exact count.
const AXIS_WIDTH = 44

const formatBucket = (bucket: string, granularity: 'hour' | 'day') => {
  const d = new Date(bucket.replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) return bucket
  if (granularity === 'hour') {
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric' })
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const userLabel = (u: { user_id: number | null; name: string | null; email: string | null }) => {
  if (u.user_id == null) return 'Unattributed'
  return u.name || u.email || `User ${u.user_id}`
}

const cacheHitPctOf = (r: { prompt_tokens: number; cached_tokens: number }): number | null =>
  r.prompt_tokens > 0 ? (r.cached_tokens / r.prompt_tokens) * 100 : null

// The horizontal scroller every table on this page sits in. On a phone each
// table is two to three times the card's width, and a column cut off at the
// card edge looks like the end of the table, so the right edge fades while
// there is more to swipe to. Horizontal only: a table grows with the page
// rather than scrolling inside it, since a vertical scroller nested in a
// horizontal one inside the page's own scroll is miserable on touch.
const TableScroller: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const scroll = useHorizontalOverflow<HTMLDivElement>()
  return (
    <div ref={scroll.ref} className="overflow-x-auto" style={fadeRightStyle(scroll)}>
      {children}
    </div>
  )
}

// One of the two aggregate breakdowns (by model, by call type). Same columns
// for both so the two tables read as a pair; the first column is whatever the
// rows are grouped by. Rows sum to the totals above, since the endpoint
// applies the selected-user filter to these the same way it does to `total`.
const BreakdownTable: React.FC<{
  title: string
  subtitle: string
  groupLabel: string
  rows: { key: string; label: React.ReactNode; stats: LlmUsageBreakdown }[]
  emptyText: string
}> = ({ title, subtitle, groupLabel, rows, emptyText }) => (
  <div className="bg-white rounded-lg shadow flex flex-col min-w-0">
    <div className="px-6 py-4 border-b border-gray-200">
      <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
      <p className="text-sm text-gray-500">{subtitle}</p>
    </div>
    {/* Tight padding and `whitespace-nowrap` throughout: the group column holds
        long unbroken identifiers like `checkin_feature_extraction`, and six
        columns in a half-width panel leave it too narrow to wrap in. Overflow
        scrolls horizontally instead of shattering the label mid-word. */}
    <TableScroller>
      <table className="w-full text-sm whitespace-nowrap">
        <thead className="bg-gray-50 text-left text-gray-600">
          <tr>
            <th className="pl-6 pr-3 py-2 font-medium">{groupLabel}</th>
            <th className="px-3 py-2 font-medium">Effort</th>
            <th className="px-3 py-2 font-medium text-right">Input</th>
            <th className="px-3 py-2 font-medium text-right">Output</th>
            <th className="px-3 py-2 font-medium text-right">Reasoning</th>
            <th className="px-3 py-2 font-medium text-right">Cache hit</th>
            <th className="pl-3 pr-6 py-2 font-medium text-right">Calls</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const hit = cacheHitPctOf(r.stats)
            return (
              <tr key={r.key} className="border-t border-gray-100">
                <td className="pl-6 pr-3 py-2 font-mono text-xs text-gray-800">{r.label}</td>
                <td className="px-3 py-2">
                  <EffortChip effort={r.stats.reasoning_effort} />
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.stats.prompt_tokens)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.stats.completion_tokens)}</td>
                <td
                  className={`px-3 py-2 text-right tabular-nums ${
                    r.stats.reasoning_tokens > 0 ? 'text-violet-700 font-medium' : 'text-gray-400'
                  }`}
                >
                  {fmt(r.stats.reasoning_tokens)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {hit == null ? '—' : `${hit.toFixed(1)}%`}
                </td>
                <td className="pl-3 pr-6 py-2 text-right tabular-nums">{fmt(r.stats.calls)}</td>
              </tr>
            )
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-6 py-6 text-center text-gray-500">
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </TableScroller>
  </div>
)

const formatCallTime = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

const callCacheHitPct = (c: LlmRecentCall): number | null => {
  if (!c.prompt_tokens || c.prompt_tokens <= 0) return null
  const cached = c.cached_tokens ?? 0
  return (cached / c.prompt_tokens) * 100
}

// Aggregate stats across a root row and its loop iterations. For non-loop
// calls (no children) this returns the root's own stats unchanged.
interface CallAggregate {
  iterations: number  // 1 + children.length
  prompt_tokens: number | null
  cached_tokens: number | null
  cache_creation_tokens: number | null
  completion_tokens: number | null
  duration_ms: number | null
  cache_hit_pct: number | null
  all_success: boolean | null
}

const aggregateCall = (c: LlmRecentCall): CallAggregate => {
  const iters: LlmRecentCall[] = [c, ...(c.children ?? [])]
  const sumOf = (key: keyof LlmRecentCall): number | null => {
    let total = 0
    let anyPresent = false
    for (const it of iters) {
      const v = it[key]
      if (typeof v === 'number') {
        total += v
        anyPresent = true
      }
    }
    return anyPresent ? total : null
  }
  const prompt = sumOf('prompt_tokens')
  const cached = sumOf('cached_tokens')
  const allSuccess = iters.every((it) => it.success !== false)
  const anyExplicitSuccess = iters.some((it) => it.success != null)
  return {
    iterations: iters.length,
    prompt_tokens: prompt,
    cached_tokens: cached,
    cache_creation_tokens: sumOf('cache_creation_tokens'),
    completion_tokens: sumOf('completion_tokens'),
    duration_ms: sumOf('duration_ms'),
    cache_hit_pct:
      prompt && prompt > 0 ? ((cached ?? 0) / prompt) * 100 : null,
    all_success: anyExplicitSuccess ? allSuccess : null,
  }
}

const CallSection: React.FC<{
  title: string
  content: string | null
  variant?: 'default' | 'error'
}> = ({ title, content, variant = 'default' }) => (
  <div className="mb-3 last:mb-0">
    <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">{title}</div>
    {content == null || content === '' ? (
      <div className="text-xs text-gray-400 italic">empty</div>
    ) : (
      <pre
        className={`text-xs whitespace-pre-wrap break-words rounded border p-3 max-h-96 overflow-y-auto ${
          variant === 'error'
            ? 'bg-rose-50 border-rose-200 text-rose-800'
            : 'bg-white border-gray-200 text-gray-800'
        }`}
      >
        {content}
      </pre>
    )}
  </div>
)

const MetaItem: React.FC<{
  label: string
  value: React.ReactNode
  mono?: boolean
}> = ({ label, value, mono }) => (
  <div className="min-w-0">
    <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">{label}</div>
    <div
      className={`text-xs text-gray-800 break-all ${mono ? 'font-mono' : ''}`}
      title={typeof value === 'string' ? value : undefined}
    >
      {value === null || value === undefined || value === '' ? (
        <span className="text-gray-300">—</span>
      ) : (
        value
      )}
    </div>
  </div>
)

// Prompt/response detail for one call. Extracted so the inline expansion on
// the recent-calls table and the per-iteration expansion inside a loop tree
// share the same layout.
const CallDetailsPanel: React.FC<{ call: LlmRecentCall }> = ({ call: c }) => (
  <>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2 mb-3">
      <MetaItem label="ID" value={String(c.id)} mono />
      <MetaItem
        label="Created"
        value={c.created_at ? new Date(c.created_at).toLocaleString() : null}
      />
      <MetaItem label="Provider" value={c.provider} />
      <MetaItem label="Model" value={c.model} mono />
      <MetaItem label="Call type" value={c.call_type} mono />
      <MetaItem label="Route key" value={c.route_key} mono />
      <MetaItem label="Effort" value={c.reasoning_effort} mono />
      <MetaItem label="Response ID" value={c.response_id} mono />
      <MetaItem
        label="Duration"
        value={c.duration_ms != null ? `${fmt(c.duration_ms)} ms` : null}
      />
      <MetaItem
        label="Tokens (est.)"
        value={c.token_count != null ? fmt(c.token_count) : null}
      />
      <MetaItem
        label="Input tokens"
        value={c.prompt_tokens != null ? fmt(c.prompt_tokens) : null}
      />
      <MetaItem
        label="Cached"
        value={c.cached_tokens != null ? fmt(c.cached_tokens) : null}
      />
      <MetaItem
        label="Cache writes"
        value={c.cache_creation_tokens != null ? fmt(c.cache_creation_tokens) : null}
      />
      <MetaItem
        label="Output tokens"
        value={c.completion_tokens != null ? fmt(c.completion_tokens) : null}
      />
      <MetaItem
        label="Reasoning tokens"
        value={c.reasoning_tokens != null ? fmt(c.reasoning_tokens) : null}
      />
      <MetaItem
        label="Success"
        value={
          c.success == null ? null : c.success ? (
            <span className="text-green-700">ok</span>
          ) : (
            <span className="text-rose-700">failed</span>
          )
        }
      />
    </div>
    <div className="text-xs text-gray-400 italic mb-3">
      Prompts &gt;1000 chars are stored as first&nbsp;1000 + last&nbsp;1000 with a clipping marker, so the cache-relevant prefix is preserved (unless log_full_prompts is enabled in site_config).
    </div>

    <CallSection title="System prompt" content={c.system_prompt} />
    <CallSection title="User prompt" content={c.user_prompt} />
    <CallSection title="Response" content={c.response} />
    {c.error_message && (
      <CallSection title="Error" content={c.error_message} variant="error" />
    )}
  </>
)

// One iteration inside a loop parse's expanded tree. Renders a compact
// header (iter index, call_type, tokens, duration, cache hit) that toggles
// the full details panel below.
const IterationRow: React.FC<{
  iter: number
  call: LlmRecentCall
  expanded: boolean
  onToggle: () => void
}> = ({ iter, call, expanded, onToggle }) => {
  const hit = callCacheHitPct(call)
  const failed = call.success === false
  return (
    <div className="border border-gray-200 rounded bg-white">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center gap-3 px-3 py-1.5 text-xs text-left ${
          failed ? 'bg-rose-50 hover:bg-rose-100' : 'hover:bg-gray-50'
        }`}
      >
        <span className="text-gray-400 select-none w-3">{expanded ? '▾' : '▸'}</span>
        <span className="font-mono text-gray-500 w-10">#{iter}</span>
        <span className="text-gray-700 truncate flex-1 min-w-0" title={call.call_type ?? ''}>
          {failed && <span className="text-rose-700 mr-1">✕</span>}
          {call.call_type ?? '—'}
        </span>
        <span className="tabular-nums text-gray-600 whitespace-nowrap">
          in {call.prompt_tokens != null ? fmt(call.prompt_tokens) : '—'}
        </span>
        <span className="tabular-nums text-gray-600 whitespace-nowrap">
          out {call.completion_tokens != null ? fmt(call.completion_tokens) : '—'}
        </span>
        <span className="tabular-nums text-gray-600 whitespace-nowrap w-16 text-right">
          {hit == null ? '—' : `${hit.toFixed(0)}%`} cache
        </span>
        <span className="tabular-nums text-gray-500 whitespace-nowrap w-20 text-right">
          {call.duration_ms != null ? `${fmt(call.duration_ms)} ms` : '—'}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-gray-200 px-3 py-3">
          <CallDetailsPanel call={call} />
        </div>
      )}
    </div>
  )
}

// The categorical hue order from the `dataviz` skill's validated palette,
// used by every chart on this page. The page's original blue/violet pair
// (#3b82f6 / #8b5cf6) is ΔE 1.3 apart under deuteranopia and 12.0 for normal
// vision — below the 15 floor. A stacked area survives that on position;
// overlapping lines, which every split view here draws, do not.
//
// Order is fixed and never cycled by rank, so a series keeps its colour when
// a filter changes how many of them there are. Past the first few, the
// validated order's later slots pair badly under `--pairs all`; nothing here
// draws more than four series today.
const SERIES_COLORS = [
  '#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948',
]

// Round up to a readable axis top (1, 2 or 5 x 10^n) so the scale ends on a
// number worth reading rather than on the data's exact maximum.
export const niceCeiling = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return 0
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalized = value / magnitude
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return step * magnitude
}

// The largest value across only the series still switched on. Hiding a
// dominant series has to rescale the axis — otherwise the survivors stay
// squashed against the baseline and toggling one off achieves nothing, which
// is most of the reason to have toggles at all.
export const visibleMax = (
  rows: Record<string, any>[],
  keys: string[],
  hidden: Set<string>,
  stacked = false,
) => {
  let max = 0
  rows.forEach((row) => {
    const values = keys.filter((k) => !hidden.has(k)).map((k) => Number(row[k]) || 0)
    const candidate = stacked
      ? values.reduce((a, b) => a + b, 0)
      : Math.max(0, ...values)
    if (candidate > max) max = candidate
  })
  return max
}

// The numbers behind the rate limits: what each model's worst minute in the
// window was, what it is doing right now, and the two caps — the one enforced
// here and the one the provider reports. The *graph* of this lives in the
// usage chart above, under its tokens/min measure; keeping a second chart of
// the same series here was duplication, so this card is the table only.
const RateLimitPanel: React.FC<{
  rows: LlmModelHeadroom[]
  rollingSeconds: number
  windowLabel: string
  // The one panel "Hide test users" does not narrow, so it says so while the
  // box is ticked rather than letting the numbers disagree silently.
  hideTest: boolean
  loading: boolean
  error: string | null
  onRefresh: () => void
}> = ({ rows, rollingSeconds, windowLabel, hideTest, loading, error, onRefresh }) => {
  const mismatched = rows.filter((r) => r.upstream_mismatch)
  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold text-gray-800">Rate limits</h2>
          <p className="text-sm text-gray-500">
            Per model, {windowLabel}. Peak is the worst {rollingSeconds}-second window,
            which is what the cap applies to. Upstream limits are re-read from the
            provider at most once a day.
            {hideTest && ' Test users are counted here — they use the same capacity.'}
          </p>
        </div>
        <motion.button
          onClick={onRefresh}
          disabled={loading}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </motion.button>
      </div>

      {mismatched.length > 0 && (
        <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-900">
          The provider now reports a different limit than this code declares for{' '}
          {mismatched.map((r) => r.model).join(', ')}. Update
          <span className="font-mono text-xs"> upstream_tokens_per_minute </span>
          in <span className="font-mono text-xs">OPENAI_SELECTABLE_MODELS</span>, and the
          enforced cap with it.
        </div>
      )}

      {error && <div className="px-6 py-3 text-sm text-rose-700">{error}</div>}

      <TableScroller>
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="pl-6 pr-3 py-2 font-medium">Model</th>
              <th className="px-3 py-2 font-medium text-right">Peak {rollingSeconds}s</th>
              <th className="px-3 py-2 font-medium">Of cap</th>
              <th className="px-3 py-2 font-medium">Peak at</th>
              <th className="px-3 py-2 font-medium text-right">Now</th>
              <th className="px-3 py-2 font-medium text-right">Enforced cap</th>
              <th className="px-3 py-2 font-medium text-right">Provider allows</th>
              <th className="pl-3 pr-6 py-2 font-medium text-right">Limit read</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const pct = r.pct_of_cap
              // 80% is where PROVIDER_WARN_FRACTION starts logging, so the
              // page and the journal agree on what "close" means.
              const hot = pct != null && pct >= 80
              return (
                <tr key={`${r.provider}/${r.model ?? 'none'}`} className="border-t border-gray-100">
                  <td className="pl-6 pr-3 py-2 font-mono text-xs text-gray-800">
                    {r.model ?? <span className="text-gray-400">unattributed</span>}
                    {r.cap == null && (
                      <span className="ml-2 text-gray-400" title="Not in OPENAI_SELECTABLE_MODELS — capped by the provider-wide fallback">
                        undeclared
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.peak)}</td>
                  <td className="px-3 py-2">
                    {pct == null ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <div className="flex items-center gap-2 min-w-[7rem]">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded overflow-hidden">
                          <div
                            className={`h-full ${hot ? 'bg-amber-500' : 'bg-emerald-500'}`}
                            style={{ width: `${Math.min(100, Math.max(pct, pct > 0 ? 2 : 0))}%` }}
                          />
                        </div>
                        <span
                          className={`tabular-nums text-xs ${hot ? 'text-amber-700 font-medium' : 'text-gray-500'}`}
                        >
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500 text-xs">
                    {r.peak_at ? new Date(r.peak_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                    {fmt(r.used_now)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.cap == null ? '—' : fmt(r.cap)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.upstream_observed == null ? (
                      <span className="text-gray-400">unknown</span>
                    ) : (
                      <span className={r.upstream_mismatch ? 'text-amber-700 font-medium' : ''}>
                        {fmt(r.upstream_observed)}
                      </span>
                    )}
                  </td>
                  <td className="pl-3 pr-6 py-2 text-right text-gray-500 text-xs">
                    {r.observed_at ? new Date(r.observed_at).toLocaleString() : '—'}
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-6 text-center text-gray-500">
                  {loading ? 'Loading…' : 'No models declared.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableScroller>
    </div>
  )
}

const LlmUsagePage: React.FC = () => {
  const [window, setWindow] = useState<LlmUsageWindow>('7d')
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  // Off by default: the page's job is to account for every token spent, and a
  // filtered view that looked like the whole picture would be the more
  // expensive mistake. Applies to everything but the headroom table.
  const [hideTest, setHideTest] = useState(false)
  const [data, setData] = useState<LlmUsageResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // How the volume chart is divided. One dimension at a time on purpose:
  // model x effort together is six-plus series, past the point where a line
  // chart communicates anything.
  const [split, setSplit] = useState<'total' | 'model' | 'model_effort'>('total')
  // Volume answers "what did it cost", rate answers "how close to being
  // throttled". Only the second is commensurable with a cap, which is why the
  // cap lines appear in one measure and not the other.
  const [measure, setMeasure] = useState<'tokens' | 'rate'>('tokens')
  // Series the viewer has clicked off in the legend. Keyed by series name, so
  // a hidden model stays hidden across a split or window change.
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())
  // Off by default: the axis follows the data, so a cap far above it simply
  // is not drawn. On, the axis stretches to the highest cap, which is the
  // "how much room is left" view at the cost of flattening the data.
  const [fitCaps, setFitCaps] = useState(false)
  const [headroom, setHeadroom] = useState<LlmModelHeadroom[]>([])
  const [rollingSeconds, setRollingSeconds] = useState(60)
  const [headroomLoading, setHeadroomLoading] = useState(false)
  const [headroomError, setHeadroomError] = useState<string | null>(null)
  // The per-user table's own paging and filtering, on its own endpoint so a
  // keystroke here doesn't re-run the chart's eight aggregates. `userQuery` is
  // what the input holds; `userFilter` is the debounced value a request uses.
  const [userQuery, setUserQuery] = useState('')
  const [userFilter, setUserFilter] = useState('')
  const [userOffset, setUserOffset] = useState(0)
  const [byUser, setByUser] = useState<LlmUsageByUser[]>([])
  const [byUserMatched, setByUserMatched] = useState(0)
  const [byUserLoading, setByUserLoading] = useState(false)
  const [byUserError, setByUserError] = useState<string | null>(null)
  const [recentCalls, setRecentCalls] = useState<LlmRecentCall[]>([])
  const [recentError, setRecentError] = useState<string | null>(null)
  const [recentLoading, setRecentLoading] = useState(false)
  const [expandedCallId, setExpandedCallId] = useState<number | null>(null)
  // Per-row state for "which iteration is open inside this loop's tree."
  // Keyed by root call id; defaults to the root iteration (1) being open.
  const [expandedIterByRoot, setExpandedIterByRoot] = useState<Record<number, number | null>>({})
  // Keyset paging: pageBefore is the `before_id` cursor used for the current
  // page; pageStack holds prior cursors so Previous can pop back. nextBeforeId
  // comes from the server when more rows match the filter.
  const [pageBefore, setPageBefore] = useState<number | null>(null)
  const [pageStack, setPageStack] = useState<(number | null)[]>([])
  const [nextBeforeId, setNextBeforeId] = useState<number | null>(null)
  // Threshold for "new since last refresh" highlight. Updated only by the
  // Refresh button so it stays meaningful across pagination too.
  const [highlightAfterId, setHighlightAfterId] = useState<number | null>(null)
  // Running max id ever shown — used to compute the new highlight threshold
  // when the user clicks Refresh.
  const [maxSeenId, setMaxSeenId] = useState<number | null>(null)
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null)

  const fetchRecent = async (beforeId: number | null) => {
    setRecentLoading(true)
    setRecentError(null)
    try {
      const res = await getRecentLlmCalls({
        limit: 50,
        userId: selectedUserId,
        beforeId,
        hideTest,
      })
      setRecentCalls(res.calls)
      setNextBeforeId(res.next_before_id)
      if (res.calls.length > 0) {
        const pageMax = Math.max(...res.calls.map((c) => c.id))
        setMaxSeenId((cur) => (cur == null ? pageMax : Math.max(cur, pageMax)))
      }
    } catch (e: any) {
      setRecentError(e?.response?.data?.detail || e.message || 'Failed to load')
    } finally {
      setRecentLoading(false)
    }
  }

  const refreshRecent = async () => {
    // Highlight everything newer than the highest id we've ever shown.
    setHighlightAfterId(maxSeenId)
    setPageBefore(null)
    setPageStack([])
    await fetchRecent(null)
    setLastRefreshedAt(new Date())
  }

  const goNextPage = () => {
    if (nextBeforeId == null) return
    setPageStack((s) => [...s, pageBefore])
    setPageBefore(nextBeforeId)
    fetchRecent(nextBeforeId)
  }

  const goPrevPage = () => {
    if (pageStack.length === 0) return
    const prev = pageStack[pageStack.length - 1]
    setPageStack((s) => s.slice(0, -1))
    setPageBefore(prev)
    fetchRecent(prev)
  }

  const fetchHeadroom = async () => {
    setHeadroomLoading(true)
    setHeadroomError(null)
    try {
      const res = await getLlmRateHeadroom(window)
      setHeadroom(res.models)
      setRollingSeconds(res.rolling_seconds)
    } catch (e: any) {
      setHeadroomError(e?.response?.data?.detail || e.message || 'Failed to load')
    } finally {
      setHeadroomLoading(false)
    }
  }

  // Follows the window selector, like everything else on the page. Not polled:
  // a page left open would ask the provider's limits question on a loop for no
  // one's benefit, and the Refresh button covers the live column.
  useEffect(() => {
    fetchHeadroom()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window])

  // Reset to page 1 (without changing the highlight) when either filter
  // changes. Initial mount: selectedUserId is null and this fires once.
  useEffect(() => {
    setPageBefore(null)
    setPageStack([])
    fetchRecent(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, hideTest])

  // Debounced, and it resets to the first page: typing an email should not
  // fire a query per keystroke, nor leave you on page 4 of a new result set.
  useEffect(() => {
    const t = setTimeout(() => {
      setUserFilter(userQuery.trim())
      setUserOffset(0)
    }, 250)
    return () => clearTimeout(t)
  }, [userQuery])

  // Deliberately not keyed on selectedUserId: the table is how you select a
  // user, so it keeps showing everyone while one row is active.
  useEffect(() => {
    let cancelled = false
    setByUserLoading(true)
    setByUserError(null)
    getLlmUsageByUser({
      window,
      q: userFilter,
      offset: userOffset,
      limit: BY_USER_PAGE_SIZE,
      hideTest,
    })
      .then((res) => {
        if (cancelled) return
        setByUser(res.rows)
        setByUserMatched(res.matched)
      })
      .catch((e) => {
        if (!cancelled) {
          setByUserError(e?.response?.data?.detail || e.message || 'Failed to load')
        }
      })
      .finally(() => {
        if (!cancelled) setByUserLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [window, userFilter, userOffset, hideTest])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getLlmUsage(window, selectedUserId, hideTest)
      .then((res) => {
        if (!cancelled) setData(res)
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || 'Failed to load')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [window, selectedUserId, hideTest])

  // From the response rather than from the table, which no longer holds every
  // user. Falls back to the bare id in the two cases the response can't name
  // them — a user with no `users` row, and the moment between picking a new
  // user and their response arriving. Never null while a user is selected, so
  // a filtered chart can't read as the unfiltered one.
  const selectedUser = useMemo(() => {
    if (selectedUserId == null) return null
    const named = data?.user_id === selectedUserId ? data.selected_user : null
    return named ?? { user_id: selectedUserId, name: null, email: null }
  }, [data, selectedUserId])

  const chartData = useMemo(() => {
    if (!data) return []
    return data.time_series.map((b) => ({
      bucket: b.bucket,
      label: formatBucket(b.bucket, data.bucket),
      prompt_tokens: b.prompt_tokens,
      completion_tokens: b.completion_tokens,
    }))
  }, [data])

  // Pivot the long-format split rows into recharts' wide shape. Series order
  // is stable (first appearance) so a colour belongs to a model or an effort,
  // never to its rank in this window's data.
  const splitChart = useMemo(() => {
    if (!data || split === 'total') return { keys: [] as string[], rows: [] as any[] }
    const source: LlmUsageSplitBucket[] =
      split === 'model' ? data.time_series_by_model : data.time_series_by_model_effort
    const keys: string[] = []
    const byBucket = new Map<string, Record<string, number | string>>()
    source.forEach((p) => {
      const key = p.series_key ?? 'none'
      if (!keys.includes(key)) keys.push(key)
      const row = byBucket.get(p.bucket) ?? {
        bucket: p.bucket,
        label: formatBucket(p.bucket, data.bucket),
      }
      row[key] = ((row[key] as number) ?? 0) + p.tokens
      byBucket.set(p.bucket, row)
    })
    const rows = [...byBucket.values()].sort((a, b) =>
      String(a.bucket).localeCompare(String(b.bucket)),
    )
    // Zero-fill, so a model that went quiet reads as zero rather than as a
    // line that jumps the gap.
    rows.forEach((r) => keys.forEach((k) => { if (r[k] == null) r[k] = 0 }))
    return { keys, rows }
  }, [data, split])

  // Peak tokens/min per bucket for the selected split. Same pivot as the
  // volume splits; "total" collapses to one series whose key the backend
  // writes as 'all'.
  const rateChart = useMemo(() => {
    if (!data) return { keys: [] as string[], rows: [] as any[] }
    const source = data.rate_series?.[split] ?? []
    const keys: string[] = []
    const byBucket = new Map<string, Record<string, number | string>>()
    source.forEach((p) => {
      const key = split === 'total' ? 'All models' : p.series_key ?? 'none'
      if (!keys.includes(key)) keys.push(key)
      const row = byBucket.get(p.bucket) ?? {
        bucket: p.bucket,
        label: formatBucket(p.bucket, data.bucket),
      }
      row[key] = Math.max((row[key] as number) ?? 0, p.peak)
      byBucket.set(p.bucket, row)
    })
    const rows = [...byBucket.values()].sort((a, b) =>
      String(a.bucket).localeCompare(String(b.bucket)),
    )
    rows.forEach((r) => keys.forEach((k) => { if (r[k] == null) r[k] = 0 }))
    return { keys, rows }
  }, [data, split])

  const maxCap = useMemo(
    () => Math.max(0, ...(data?.model_caps ?? []).map((c) => c.cap)),
    [data],
  )

  // One domain per chart, computed from the visible series rather than left
  // to recharts, which counts hidden ones.
  const rateDomainMax = useMemo(() => {
    if (fitCaps && maxCap > 0) return maxCap
    return niceCeiling(visibleMax(rateChart.rows, rateChart.keys, hiddenSeries)) || maxCap
  }, [rateChart, hiddenSeries, fitCaps, maxCap])

  const splitDomainMax = useMemo(
    () => niceCeiling(visibleMax(splitChart.rows, splitChart.keys, hiddenSeries)),
    [splitChart, hiddenSeries],
  )

  const stackedDomainMax = useMemo(
    () =>
      niceCeiling(
        visibleMax(chartData, ['prompt_tokens', 'completion_tokens'], hiddenSeries, true),
      ),
    [chartData, hiddenSeries],
  )

  // Caps the rate axis cannot show at its current scale. Named under the
  // chart rather than clamped to the top edge, which would read as "at the
  // limit" when the truth is the opposite.
  const offScaleCaps = useMemo(
    () => (data?.model_caps ?? []).filter((c) => c.cap > rateDomainMax),
    [data, rateDomainMax],
  )

  const toggleSeries = (name: string) =>
    setHiddenSeries((cur) => {
      const next = new Set(cur)
      next.has(name) ? next.delete(name) : next.add(name)
      return next
    })

  // Recharts' own legend, made clickable, with the hidden state shown by
  // dimming rather than by removing the entry — an entry you cannot find
  // again is a series you cannot turn back on.
  const clickableLegend = (
    <Legend
      onClick={(e: any) => toggleSeries(String(e?.dataKey ?? e?.value ?? ''))}
      // Label in ink, not in the series hue. Recharts colours legend text
      // with the series colour by default, and three hues in SERIES_COLORS
      // sit under 3:1 against this surface — as a swatch beside dark text
      // that is fine, as the text itself it is not. The swatch carries
      // identity; the words stay readable.
      formatter={(value: string, entry: any) => (
        <span
          className={`cursor-pointer select-none ${
            hiddenSeries.has(String(entry?.dataKey ?? value))
              ? 'text-gray-400 line-through'
              : 'text-gray-700'
          }`}
        >
          {value}
        </span>
      )}
      wrapperStyle={{ cursor: 'pointer' }}
    />
  )

  const cacheHitPct = useMemo(() => {
    if (!data || data.total.prompt_tokens === 0) return null
    return (data.total.cached_tokens / data.total.prompt_tokens) * 100
  }, [data])

  // Hide the "Cache new" (cache_creation_tokens) column when nothing on the
  // current page reports it — OpenAI never reports it, so on OpenAI-only
  // pages the column is just a wall of "—" eating ~80px of width.
  const showCacheNewCol = useMemo(
    () => recentCalls.some((c) => (c.cache_creation_tokens ?? 0) > 0),
    [recentCalls],
  )

  return (
    <div className="flex flex-col min-h-screen bg-blush-light">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="min-w-0">
            <Link to="/admin" className="text-sm text-amber-800 hover:underline">← Admin</Link>
            <h1 className="text-3xl font-bold text-gray-800 mt-1">LLM Usage</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 mr-2 text-sm text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hideTest}
                onChange={(e) => {
                  setHideTest(e.target.checked)
                  // Both are a new result set for the per-user table, and the
                  // selected row may not be among its users any more — a chart
                  // headed by a user the table no longer lists reads as a bug.
                  setUserOffset(0)
                  setSelectedUserId(null)
                }}
                className="accent-amber-500"
              />
              Hide test users
            </label>
            {WINDOWS.map((w) => (
              <button
                key={w.value}
                onClick={() => {
                  setWindow(w.value)
                  // A new window is a new result set for the per-user table.
                  setUserOffset(0)
                }}
                className={`px-3 py-1.5 rounded-md text-sm border whitespace-nowrap ${
                  window === w.value
                    ? 'bg-action text-plum border-amber-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-gray-800">
                {selectedUser ? `Usage — ${userLabel(selectedUser)}` : 'Overall usage'}
              </h2>
              <p className="text-sm text-gray-500">
                Tokens, not dollars — there is no price table. Calls logged without a
                provider (backend test-suite writes) are excluded throughout.
              </p>
            </div>
            {selectedUser && (
              <button
                onClick={() => setSelectedUserId(null)}
                className="text-sm text-amber-800 hover:underline"
              >
                Clear filter
              </button>
            )}
          </div>

          {data && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4 text-sm">
              <div>
                <div className="text-gray-500">Input tokens</div>
                <div className="text-2xl font-semibold">{fmt(data.total.prompt_tokens)}</div>
              </div>
              <div>
                <div className="text-gray-500">Output tokens</div>
                <div className="text-2xl font-semibold">{fmt(data.total.completion_tokens)}</div>
              </div>
              <div>
                <div className="text-gray-500">Reasoning tokens</div>
                <div className="text-2xl font-semibold">{fmt(data.total.reasoning_tokens)}</div>
                <div className="text-xs text-gray-400">billed inside output</div>
              </div>
              <div>
                <div className="text-gray-500">Cache hit</div>
                <div className="text-2xl font-semibold">
                  {cacheHitPct == null ? '—' : `${cacheHitPct.toFixed(1)}%`}
                </div>
                <div className="text-xs text-gray-400">{fmt(data.total.cached_tokens)} cached</div>
              </div>
              <div>
                <div className="text-gray-500">Calls</div>
                <div className="text-2xl font-semibold">{fmt(data.total.calls)}</div>
              </div>
            </div>
          )}

          {/* Split by one dimension at a time. "Total" keeps the input/output
              story, which is the only view where the two measures are
              separable; the other two trade that for composition, since
              stacking input and output *and* splitting by model would be four
              to six stacked bands per bucket. Every view sums to the same
              bucket totals. */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Measure</span>
              {([
                ['tokens', 'Tokens'],
                ['rate', `Peak tokens/${rollingSeconds}s`],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setMeasure(value)}
                  className={`px-2 py-0.5 rounded border ${
                    measure === value
                      ? 'bg-amber-400 border-amber-400 text-gray-900 font-medium'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Split by</span>
              {([
                ['total', 'Total'],
                ['model', 'Model'],
                ['model_effort', 'Model + effort'],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setSplit(value)}
                  className={`px-2 py-0.5 rounded border ${
                    split === value
                      ? 'bg-amber-400 border-amber-400 text-gray-900 font-medium'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {measure === 'rate' && (
              <label className="flex items-center gap-1.5 text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={fitCaps}
                  onChange={(e) => setFitCaps(e.target.checked)}
                  className="accent-amber-500"
                />
                Fit caps
              </label>
            )}
            <span className="text-xs text-gray-400">
              {measure === 'rate'
                ? `Worst ${rollingSeconds}s window in each bucket — the quantity the caps apply to.`
                : 'Total spend per bucket. Caps are a per-minute quantity, so they are not comparable to this measure.'}
            </span>
          </div>

          <div className="h-72">
            {loading && !data ? (
              <div className="flex items-center justify-center h-full text-gray-500">Loading…</div>
            ) : chartData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                No data in this window.
              </div>
            ) : measure === 'rate' ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rateChart.rows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                  {/* Scaled to the visible series, so hiding one rescales the
                      axis. A cap far above the data is simply not drawn —
                      named under the chart instead — because stretching the
                      axis to reach it flattens everything worth looking at.
                      "Fit caps" is there for when the ceiling is the point. */}
                  <YAxis
                    domain={[0, rateDomainMax || 'auto']}
                    tick={{ fontSize: 11 }}
                    tickFormatter={formatCompact}
                    tickLine={false}
                    width={AXIS_WIDTH}
                  />
                  <Tooltip formatter={(v: number) => `${fmt(v)} tokens/min`} />
                  {clickableLegend}
                  {(data?.model_caps ?? [])
                    .filter((c) => c.cap <= rateDomainMax)
                    .map((c) => (
                      <ReferenceLine
                        key={`cap-${c.model}`}
                        y={c.cap}
                        stroke="#9ca3af"
                        strokeDasharray="4 4"
                        label={{
                          value: `${c.model} cap`,
                          position: 'insideTopRight',
                          fontSize: 10,
                          fill: '#6b7280',
                        }}
                      />
                    ))}
                  {rateChart.keys.map((key, i) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={key}
                      hide={hiddenSeries.has(key)}
                      stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : split === 'total' ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                  <YAxis
                    domain={[0, stackedDomainMax || 'auto']}
                    tick={{ fontSize: 11 }}
                    tickFormatter={formatCompact}
                    tickLine={false}
                    width={AXIS_WIDTH}
                  />
                  <Tooltip formatter={(v: number) => fmt(v)} />
                  {clickableLegend}
                  <Area
                    type="monotone"
                    dataKey="prompt_tokens"
                    name="Input"
                    hide={hiddenSeries.has('prompt_tokens') || hiddenSeries.has('Input')}
                    stackId="1"
                    stroke={SERIES_COLORS[0]}
                    fill={SERIES_COLORS[0]}
                    fillOpacity={0.25}
                  />
                  <Area
                    type="monotone"
                    dataKey="completion_tokens"
                    name="Output"
                    hide={hiddenSeries.has('completion_tokens') || hiddenSeries.has('Output')}
                    stackId="1"
                    stroke={SERIES_COLORS[1]}
                    fill={SERIES_COLORS[1]}
                    fillOpacity={0.25}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={splitChart.rows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                  <YAxis
                    domain={[0, splitDomainMax || 'auto']}
                    tick={{ fontSize: 11 }}
                    tickFormatter={formatCompact}
                    tickLine={false}
                    width={AXIS_WIDTH}
                  />
                  <Tooltip formatter={(v: number) => fmt(v)} />
                  {clickableLegend}
                  {splitChart.keys.map((key, i) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={key}
                      hide={hiddenSeries.has(key)}
                      stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {measure === 'rate' && offScaleCaps.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              Off this scale: {offScaleCaps.map((c) => `${c.model} cap ${fmt(c.cap)}/min`).join(', ')}.
              Tick <span className="font-medium">Fit caps</span> to bring them into view, at
              the cost of flattening the data.
            </p>
          )}
        </div>

        <RateLimitPanel
          rows={headroom}
          rollingSeconds={rollingSeconds}
          windowLabel={(WINDOWS.find((w) => w.value === window)?.label ?? '').toLowerCase()}
          hideTest={hideTest}
          loading={headroomLoading}
          error={headroomError}
          onRefresh={fetchHeadroom}
        />

        {/* Stacked rather than side by side: six columns plus a call_type as long
            as `checkin_feature_extraction_tool_call` does not fit in half a page,
            and half-width panels pushed Reasoning out of view behind a scroll. */}
        <div className="space-y-6 mb-6">
          <BreakdownTable
            title="By model"
            subtitle={
              selectedUser
                ? `Per model and effort, for ${userLabel(selectedUser)}.`
                : 'Per model and effort, across every user in this window.'
            }
            groupLabel="Model"
            rows={(data?.by_model ?? []).map((m) => ({
              key: `${m.provider ?? '?'}/${m.model ?? '?'}/${m.reasoning_effort ?? 'none'}`,
              label: (
                <>
                  {m.model ?? 'unknown'}
                  {m.provider && <span className="ml-2 text-gray-400">{m.provider}</span>}
                </>
              ),
              stats: m,
            }))}
            emptyText="No usage in this window."
          />
          <BreakdownTable
            title="By call type"
            subtitle="Which use case is spending the tokens, and at what effort. One row per (call type, effort), so a use case mid-flip shows its before and its after. Sums to the totals above."
            groupLabel="Call type"
            rows={(data?.by_call_type ?? []).map((c) => ({
              key: `${c.call_type ?? 'null'}/${c.reasoning_effort ?? 'none'}`,
              label: c.call_type ?? 'unattributed',
              stats: c,
            }))}
            emptyText="No usage in this window."
          />
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-gray-800">By user</h2>
              <p className="text-sm text-gray-500">
                Click a row to filter the chart above. Biggest spenders first
                {hideTest
                  ? '.'
                  : '; a row with only an id is a deleted user whose calls outlived them.'}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <input
                type="search"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder="Name, email or id"
                className="w-64 px-3 py-1.5 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
              {userQuery !== '' && (
                <button
                  onClick={() => setUserQuery('')}
                  className="px-3 py-1.5 rounded-md text-sm border bg-white text-gray-700 border-gray-300 hover:bg-gray-100 whitespace-nowrap"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <TableScroller>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-6 py-2 font-medium">User</th>
                  <th className="px-6 py-2 font-medium">Email</th>
                  <th className="px-6 py-2 font-medium text-right">Input</th>
                  <th className="px-6 py-2 font-medium text-right">Output</th>
                  <th className="px-6 py-2 font-medium text-right">Cache hit</th>
                  <th className="px-6 py-2 font-medium text-right">Calls</th>
                  <th className="px-6 py-2 font-medium text-right"></th>
                </tr>
              </thead>
              <tbody>
                {byUser.map((u) => {
                  // The Unattributed row can't be selected: `user_id = null` is
                  // how the chart spells "no filter", so clicking it would read
                  // as picking that bucket and show everybody's traffic. It is
                  // the teardown's leavings — every deleted test user's calls,
                  // gathered under one heading.
                  const selectable = u.user_id != null
                  const isSelected = u.user_id === selectedUserId
                  const userCacheHit =
                    u.prompt_tokens > 0 ? (u.cached_tokens / u.prompt_tokens) * 100 : null
                  return (
                    <tr
                      key={`${u.user_id ?? 'null'}`}
                      onClick={
                        selectable
                          ? () => setSelectedUserId(isSelected ? null : u.user_id)
                          : undefined
                      }
                      className={`border-t border-gray-100 ${
                        !selectable
                          ? 'text-gray-500'
                          : isSelected
                            ? 'cursor-pointer bg-amber-50'
                            : 'cursor-pointer hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-6 py-2">{userLabel(u)}</td>
                      <td className="px-6 py-2 text-gray-500">{u.email ?? '—'}</td>
                      <td className="px-6 py-2 text-right tabular-nums">{fmt(u.prompt_tokens)}</td>
                      <td className="px-6 py-2 text-right tabular-nums">{fmt(u.completion_tokens)}</td>
                      <td className="px-6 py-2 text-right tabular-nums">
                        {userCacheHit == null ? '—' : `${userCacheHit.toFixed(1)}%`}
                      </td>
                      <td className="px-6 py-2 text-right tabular-nums">{fmt(u.calls)}</td>
                      <td className="px-6 py-2 text-right">
                        {u.user_id != null && (
                          <Link
                            to={`/admin/users/${u.user_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-amber-800 hover:underline"
                          >
                            Manage →
                          </Link>
                        )}
                      </td>
                    </tr>
                  )
                })}
                {byUserError && (
                  <tr>
                    <td colSpan={7} className="px-6 py-6 text-center text-red-700">
                      {byUserError}
                    </td>
                  </tr>
                )}
                {!byUserError && byUser.length === 0 && !byUserLoading && (
                  <tr>
                    <td colSpan={7} className="px-6 py-6 text-center text-gray-500">
                      {userFilter
                        ? `No user matches "${userFilter}" in this window.`
                        : 'No usage in this window.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableScroller>
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between gap-4 text-sm">
            <span className="text-gray-500 tabular-nums">
              {byUser.length > 0 &&
                `${fmt(userOffset + 1)}–${fmt(userOffset + byUser.length)} of ${fmt(
                  byUserMatched,
                )}`}
              {byUserLoading && <span className="ml-2 text-gray-400">Loading…</span>}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setUserOffset((o) => Math.max(0, o - BY_USER_PAGE_SIZE))}
                disabled={byUserLoading || userOffset === 0}
                className={`px-3 py-1.5 rounded-md text-sm border whitespace-nowrap ${
                  byUserLoading || userOffset === 0
                    ? 'bg-gray-100 text-gray-400 border-gray-200'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                ← Previous
              </button>
              <button
                onClick={() => setUserOffset((o) => o + BY_USER_PAGE_SIZE)}
                disabled={byUserLoading || userOffset + byUser.length >= byUserMatched}
                className={`px-3 py-1.5 rounded-md text-sm border whitespace-nowrap ${
                  byUserLoading || userOffset + byUser.length >= byUserMatched
                    ? 'bg-gray-100 text-gray-400 border-gray-200'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow mt-6">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-gray-800">
                Recent calls
                {selectedUser && (
                  <span className="ml-2 text-base font-normal text-gray-500">
                    — {userLabel(selectedUser)}
                  </span>
                )}
              </h2>
              <p className="text-sm text-gray-500">
                Page of 50. Rows highlighted in amber landed since your last refresh.
                {selectedUserId != null && ' Filtered by selected user (click Clear filter above to widen).'}
                {lastRefreshedAt && (
                  <span className="ml-1 text-gray-400">
                    Last refreshed {lastRefreshedAt.toLocaleTimeString()}.
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={goPrevPage}
                disabled={recentLoading || pageStack.length === 0}
                className={`px-3 py-1.5 rounded-md text-sm border whitespace-nowrap ${
                  recentLoading || pageStack.length === 0
                    ? 'bg-gray-100 text-gray-400 border-gray-200'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                ← Previous
              </button>
              <button
                onClick={goNextPage}
                disabled={recentLoading || nextBeforeId == null}
                className={`px-3 py-1.5 rounded-md text-sm border whitespace-nowrap ${
                  recentLoading || nextBeforeId == null
                    ? 'bg-gray-100 text-gray-400 border-gray-200'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                Next →
              </button>
              <button
                onClick={refreshRecent}
                disabled={recentLoading}
                className={`px-3 py-1.5 rounded-md text-sm border whitespace-nowrap ${
                  recentLoading
                    ? 'bg-gray-100 text-gray-400 border-gray-200'
                    : 'bg-action text-plum border-amber-600 hover:bg-action-dark'
                }`}
              >
                {recentLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>

          {recentError && (
            <div className="px-6 py-3 text-sm text-rose-700 bg-rose-50 border-b border-rose-200">
              {recentError}
            </div>
          )}

          <TableScroller>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-2 py-2 font-medium w-6"></th>
                  <th className="px-3 py-2 font-medium">Time</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">Model</th>
                  <th className="px-3 py-2 font-medium text-right">Input</th>
                  <th className="px-3 py-2 font-medium text-right">Cached</th>
                  {showCacheNewCol && (
                    <th className="px-3 py-2 font-medium text-right">Cache new</th>
                  )}
                  <th className="px-3 py-2 font-medium text-right">Output</th>
                  <th className="px-3 py-2 font-medium text-right">Cache hit</th>
                </tr>
              </thead>
              <tbody>
                {recentCalls.map((c) => {
                  const isNew = highlightAfterId != null && c.id > highlightAfterId
                  const agg = aggregateCall(c)
                  const isLoop = agg.iterations > 1
                  const hit = agg.cache_hit_pct
                  const isExpanded = expandedCallId === c.id
                  const failed = agg.all_success === false
                  const toggle = () => setExpandedCallId(isExpanded ? null : c.id)
                  const iterations: LlmRecentCall[] = [c, ...(c.children ?? [])]
                  const expandedIter = expandedIterByRoot[c.id] ?? 1
                  const setIterOpen = (n: number | null) =>
                    setExpandedIterByRoot((m) => ({ ...m, [c.id]: n }))
                  return (
                    <React.Fragment key={c.id}>
                      <tr
                        onClick={toggle}
                        className={`border-t border-gray-100 cursor-pointer ${
                          isExpanded
                            ? 'bg-amber-50'
                            : failed
                            ? 'bg-rose-50 hover:bg-rose-100'
                            : isNew
                            ? 'bg-amber-50'
                            : 'hover:bg-gray-50'
                        }`}
                        title={failed ? 'Failed call — expand for error_message' : undefined}
                      >
                        <td className="px-2 py-2 text-gray-400 text-center select-none">
                          {isExpanded ? '▾' : '▸'}
                        </td>
                        <td className={`px-3 py-2 whitespace-nowrap ${failed ? 'text-rose-700' : 'text-gray-700'}`}>
                          {failed && <span className="mr-1">✕</span>}
                          {formatCallTime(c.created_at)}
                        </td>
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                          {c.call_type ?? <span className="text-gray-400">—</span>}
                          {isLoop && (
                            <span
                              className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-900"
                              title={`${agg.iterations} iterations`}
                            >
                              ↻ {agg.iterations}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {userLabel({ user_id: c.user_id, name: c.name, email: c.email })}
                        </td>
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                          {c.model ?? '—'}
                          {c.reasoning_effort && c.reasoning_effort !== 'minimal' && (
                            <span className="ml-2">
                              <EffortChip effort={c.reasoning_effort} />
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {agg.prompt_tokens != null ? fmt(agg.prompt_tokens) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {agg.cached_tokens != null ? fmt(agg.cached_tokens) : '—'}
                        </td>
                        {showCacheNewCol && (
                          <td className="px-3 py-2 text-right tabular-nums">
                            {agg.cache_creation_tokens != null ? fmt(agg.cache_creation_tokens) : '—'}
                          </td>
                        )}
                        <td className="px-3 py-2 text-right tabular-nums">
                          {agg.completion_tokens != null ? fmt(agg.completion_tokens) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {hit == null ? '—' : `${hit.toFixed(1)}%`}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-t border-gray-100 bg-gray-50">
                          <td></td>
                          <td colSpan={showCacheNewCol ? 9 : 8} className="px-4 py-4">
                            {isLoop ? (
                              <div>
                                <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">
                                  Iterations ({agg.iterations})
                                  {agg.duration_ms != null && (
                                    <span className="ml-2 text-gray-400 normal-case font-normal tracking-normal">
                                      total {fmt(agg.duration_ms)} ms
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-col gap-1.5">
                                  {iterations.map((it, idx) => {
                                    const n = idx + 1
                                    return (
                                      <IterationRow
                                        key={it.id}
                                        iter={n}
                                        call={it}
                                        expanded={expandedIter === n}
                                        onToggle={() => setIterOpen(expandedIter === n ? null : n)}
                                      />
                                    )
                                  })}
                                </div>
                              </div>
                            ) : (
                              <CallDetailsPanel call={c} />
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
                {recentCalls.length === 0 && !recentLoading && (
                  <tr>
                    <td colSpan={showCacheNewCol ? 10 : 9} className="px-6 py-6 text-center text-gray-500">
                      No calls.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableScroller>
        </div>
      </div>
    </div>
  )
}

export default LlmUsagePage
